import React, { useState, useEffect } from 'react';
import { Box, Button, Flex, Icon, Input, Spinner, Text, useToast } from '@chakra-ui/react';
import { FiCrosshair, FiMapPin, FiSearch, FiX } from 'react-icons/fi';
import debounce from 'lodash/debounce';
import ModalShell from '../../../components/ui/ModalShell';
import { getCitySuggestions, getPlaceDetails, CitySuggestion } from '../../../services/flaskService';
import { PlaceDetails } from '../../../types';
import { getCurrentLocation } from '../../../utils/locationUtils';
import { US_STATE_ABBREVIATIONS } from '../utils';
import { createCustomToast } from '../../../features/collabs/components/CustomToast';
import { ff } from '../../../theme';

interface LocationSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLocationSelect: (location: PlaceDetails, radius: number) => void;
  currentLocation?: PlaceDetails | null;
  onClearLocation?: () => void;
  radius?: number;
  title?: string;
  /** Second line under "Use my current location". */
  currentLocationHint?: string;
}

const RADIUS_OPTIONS = [5, 10, 25, 50];

/** "Austin, TX, USA" → name "Austin", region "TX, USA". */
const splitDescription = (description: string) => {
  const i = description.indexOf(',');
  return i < 0 ? { name: description, region: '' } : { name: description.slice(0, i), region: description.slice(i + 1).trim() };
};

const RowIcon: React.FC<{ icon: React.ElementType; lime?: boolean }> = ({ icon, lime }) => (
  <Flex flex="none" w="40px" h="40px" borderRadius="12px" bg={lime ? ff.lime : ff.grey} color={lime ? ff.ink : ff.body} align="center" justify="center">
    <Icon as={icon} boxSize="20px" strokeWidth={2} aria-hidden />
  </Flex>
);

const LocationSearchModal: React.FC<LocationSearchModalProps> = ({
  isOpen,
  onClose,
  onLocationSelect,
  currentLocation,
  onClearLocation,
  radius = 25,
  title = 'Choose a location',
  currentLocationHint = 'Fill in where you are now',
}) => {
  const toast = useToast();
  const customToast = createCustomToast(toast);
  const [searchText, setSearchText] = useState('');
  const [suggestions, setSuggestions] = useState<CitySuggestion[]>([]);
  // Query the last suggestion fetch answered, so "no matches" only shows after a real empty result.
  const [fetchedFor, setFetchedFor] = useState('');
  const [localRadius, setLocalRadius] = useState(radius);
  const [selectedPlaceDetails, setSelectedPlaceDetails] = useState<PlaceDetails | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [currentCoordinates, setCurrentCoordinates] = useState<{ lat: number; lng: number } | null>(null);

  const debouncedFetchSuggestions = React.useMemo(
    () =>
      debounce(async (input: string) => {
        if (input.length < 3) {
          setSuggestions([]);
          return;
        }
        try {
          setSuggestions(await getCitySuggestions(input, currentCoordinates?.lat, currentCoordinates?.lng));
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          setSuggestions([]);
        }
        setFetchedFor(input);
      }, 500),
    [currentCoordinates]
  );

  // Reset modal state when opened/closed
  useEffect(() => {
    if (isOpen) {
      setSearchText(currentLocation?.fullAddress || '');
      setLocalRadius(radius);
      setSelectedPlaceDetails(currentLocation ?? null);
    } else {
      setSearchText('');
      setSuggestions([]);
      setFetchedFor('');
      setSelectedPlaceDetails(null);
      setCurrentCoordinates(null);
      debouncedFetchSuggestions.cancel();
    }
  }, [isOpen, currentLocation, radius]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bias suggestions toward where the creator is, when the browser allows it.
  useEffect(() => {
    if (!isOpen) return;
    navigator.geolocation.getCurrentPosition(
      (position) => setCurrentCoordinates({ lat: position.coords.latitude, lng: position.coords.longitude }),
      () => setCurrentCoordinates(null)
    );
  }, [isOpen]);

  useEffect(() => () => debouncedFetchSuggestions.cancel(), [debouncedFetchSuggestions]);

  const handleSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    setSelectedPlaceDetails(null);
    if (value.length < 3) setSuggestions([]);
    debouncedFetchSuggestions(value);
  };

  const handleClear = () => {
    setSearchText('');
    setSuggestions([]);
    setSelectedPlaceDetails(null);
  };

  const handleSubmit = () => {
    if (selectedPlaceDetails) {
      onLocationSelect(selectedPlaceDetails, localRadius);
      onClose();
    } else if (searchText === '' && onClearLocation) {
      onClearLocation();
      onClose();
    }
  };

  const handleSuggestionClick = async (suggestion: CitySuggestion) => {
    try {
      const details = await getPlaceDetails(suggestion.placeId);
      setSelectedPlaceDetails(details);
      setSearchText(suggestion.description);
      setSuggestions([]);
    } catch (error) {
      console.error('Error getting place details:', error);
    }
  };

  const handleUseCurrentLocation = async () => {
    setIsGettingLocation(true);
    try {
      const here = await getCurrentLocation();
      if (here) {
        let stateAbbr = here.state;
        if (here.country === 'United States' && here.state) {
          stateAbbr = US_STATE_ABBREVIATIONS[here.state] || here.state;
        }
        const placeDetails: PlaceDetails = {
          lat: here.lat,
          lng: here.lng,
          fullAddress: `${here.city}${stateAbbr ? `, ${stateAbbr}` : ''}, ${here.country}`,
          streetNumber: '',
          streetName: '',
          city: here.city,
          state: stateAbbr,
          country: here.country,
          zip: '',
        };
        setSelectedPlaceDetails(placeDetails);
        setSearchText(placeDetails.fullAddress);
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Error getting current location:', error);
      customToast.showErrorToast(
        'Location Access Denied',
        'Please enable location services in your browser settings to use this feature.'
      );
    } finally {
      setIsGettingLocation(false);
    }
  };

  const editing = !selectedPlaceDetails;
  const query = searchText.trim();
  const showEmpty = editing && query.length >= 3 && fetchedFor === searchText && suggestions.length === 0;
  const canClear = searchText === '' && !!onClearLocation;
  const ready = !!selectedPlaceDetails || canClear;
  const radiusOptions = RADIUS_OPTIONS.includes(localRadius) ? RADIUS_OPTIONS : [...RADIUS_OPTIONS, localRadius].sort((a, b) => a - b);

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      hideClose
      scrollBehavior="inside"
      footer={
        <Box mx="-20px" mt="12px" px="20px" pt="12px" borderTop="1px solid" borderColor={ff.line}>
          <Button
            variant="ink"
            w="100%"
            onClick={handleSubmit}
            isDisabled={!ready}
            _disabled={{ bg: ff.grey, color: ff.text2, cursor: 'not-allowed', opacity: 1 }}
          >
            {selectedPlaceDetails ? 'Apply' : canClear ? 'Clear location' : 'Pick a place to continue'}
          </Button>
        </Box>
      }
    >
      <Flex align="flex-start" justify="space-between" gap="12px">
        <Box>
          <Text as="h2" m={0} textStyle="display" fontSize="24px">{title}</Text>
          <Text mt="4px" fontSize="14px" color={ff.text2}>Search a city, county, or ZIP code.</Text>
        </Box>
        <Flex
          as="button"
          type="button"
          aria-label="Close"
          onClick={onClose}
          flex="none"
          w="44px"
          h="44px"
          borderRadius="full"
          bg={ff.grey}
          align="center"
          justify="center"
        >
          <FiX size={18} strokeWidth={2.2} />
        </Flex>
      </Flex>

      {/* Search field: grey when settled, ink outline while typing */}
      <Flex
        as="label"
        mt="18px"
        h="56px"
        pl="16px"
        pr="8px"
        gap="10px"
        align="center"
        borderRadius="16px"
        border="2px solid"
        borderColor={editing ? ff.ink : 'transparent'}
        bg={editing ? 'white' : ff.grey}
      >
        <Icon as={FiSearch} boxSize="20px" color={ff.text2} flex="none" aria-hidden />
        <Input
          variant="unstyled"
          aria-label="Location"
          placeholder="City, county, or ZIP"
          value={searchText}
          onChange={handleSearchTextChange}
          autoFocus
          h="100%"
          fontSize="17px"
          fontWeight={500}
          _placeholder={{ color: ff.muted }}
        />
        {searchText && (
          <Flex as="button" type="button" aria-label="Clear search" onClick={handleClear} flex="none" w="36px" h="36px" align="center" justify="center">
            <Flex w="22px" h="22px" borderRadius="full" bg={ff.faint} color="white" align="center" justify="center">
              <FiX size={12} strokeWidth={3} />
            </Flex>
          </Flex>
        )}
      </Flex>

      <Flex direction="column" pt="8px">
        <Flex
          as="button"
          type="button"
          onClick={handleUseCurrentLocation}
          align="center"
          gap="14px"
          minH="60px"
          py="12px"
          textAlign="left"
          borderBottom="1px solid"
          borderColor={ff.line}
        >
          <RowIcon icon={FiCrosshair} lime />
          <Flex direction="column" gap="2px" flex={1}>
            <Text fontSize="16px" fontWeight={600}>Use my current location</Text>
            <Text fontSize="13px" color={ff.text2}>{currentLocationHint}</Text>
          </Flex>
          {isGettingLocation && <Spinner size="sm" />}
        </Flex>

        {editing &&
          suggestions.map((suggestion) => {
            const { name, region } = splitDescription(suggestion.description);
            const hit = name.toLowerCase().startsWith(query.toLowerCase()) ? query.length : 0;
            return (
              <Flex
                key={suggestion.placeId}
                as="button"
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                align="center"
                gap="14px"
                minH="60px"
                py="10px"
                textAlign="left"
              >
                <RowIcon icon={FiMapPin} />
                <Flex direction="column" gap="2px" flex={1} minW={0}>
                  <Text fontSize="16px" fontWeight={500} color={ff.body} noOfLines={1}>
                    <Text as="span" fontWeight={600} color={ff.ink}>{name.slice(0, hit)}</Text>
                    {name.slice(hit)}
                  </Text>
                  {region && <Text fontSize="13px" color={ff.text2} noOfLines={1}>{region}</Text>}
                </Flex>
              </Flex>
            );
          })}

        {showEmpty && (
          <Flex direction="column" align="center" gap="6px" py="32px" px="8px" textAlign="center">
            <Text fontSize="16px" fontWeight={600}>No places match “{query}”</Text>
            <Text fontSize="14px" color={ff.text2}>Check the spelling, or try a nearby city or ZIP code.</Text>
          </Flex>
        )}

        {!editing && (
          <Flex direction="column" gap="12px" pt="20px">
            <Flex align="baseline" justify="space-between">
              <Text fontSize="16px" fontWeight={600}>Search radius</Text>
              <Text fontSize="15px" fontWeight={600} color={ff.body}>{localRadius} miles</Text>
            </Flex>
            <Box
              role="group"
              aria-label="Search radius"
              display="grid"
              gridTemplateColumns={`repeat(${radiusOptions.length}, minmax(0, 1fr))`}
              gap="6px"
              p="4px"
              bg={ff.grey}
              borderRadius="14px"
            >
              {radiusOptions.map((r) => {
                const on = r === localRadius;
                return (
                  <Box
                    key={r}
                    as="button"
                    type="button"
                    aria-pressed={on}
                    onClick={() => setLocalRadius(r)}
                    h="44px"
                    borderRadius="10px"
                    bg={on ? ff.ink : 'transparent'}
                    color={on ? 'white' : ff.body}
                    fontSize="15px"
                    fontWeight={600}
                    boxShadow={on ? '0 1px 3px rgba(0,0,0,0.2)' : 'none'}
                  >
                    {r}
                  </Box>
                );
              })}
            </Box>
          </Flex>
        )}
      </Flex>
    </ModalShell>
  );
};

export default LocationSearchModal;
